import { Fragment, SyntheticEvent } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';
import { useToggle } from '@cutwise/react-utils';
import { isNumber } from '@cutwise/utils';

import Label, { SIZE_LARGE, SIZE_MEDIUM } from '@/common/components/Label/Label';
import { CarbonProjectItem } from '@/drive/entities/DriveItem/CarbonProjectItem';
import { ProductItem } from '@/drive/entities/DriveItem/ProductItem';
import { getAccessStatusTitle } from '@/security/components/AccessStatus/AccessStatus.constants';
import GlobalAccessModal from '@/security/components/GlobalAccessModal/GlobalAccessModal';
import { getAccessById, getAccessLabelAppearance } from '@/security/constants/access';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';

import styles from './DriveEntityAccessStatus.module.css';

export type DriveAccessEntity = ProductItem | CarbonProjectItem;

export interface DriveEntityAccessStatusProps {
  item: DriveAccessEntity;
  size?: typeof SIZE_MEDIUM | typeof SIZE_LARGE;
  isCreatable?: boolean;
  className?: string;
  shouldHidePrivate?: boolean;
  onUpdate?: () => void;
}

function DriveEntityAccessStatus(props: DriveEntityAccessStatusProps) {
  const { item, size = SIZE_LARGE, isCreatable, className, shouldHidePrivate, onUpdate } = props;

  const [isAccessModalOpen, openAccessModal, closeAccessModal] = useToggle();

  if (!isNumber(item.access)) {
    return null;
  }

  if (shouldHidePrivate && item.access === GlobalAccessLevel.Restricted) {
    return null;
  }

  const type = item instanceof ProductItem ? 'product' : 'project';
  const target = item.toGlobalAccessTarget();
  const isEditable = !!isCreatable && item.canManageAccess && !!target;

  function onClickHandler(e: SyntheticEvent) {
    if (!isEditable) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    openAccessModal();
  }

  return (
    <Fragment>
      <Label
        appearance={getAccessLabelAppearance(item.access)}
        aria-label={`${item.title} access status`}
        className={cls({ [styles.interactive]: isEditable }, className)}
        onClick={onClickHandler}
        size={size}
        title={getAccessStatusTitle(type, item.access, item.dataAccess ?? undefined)}
      >
        {getAccessById(item.access, type)?.title}
      </Label>

      {target && (
        <GlobalAccessModal
          targets={[target]}
          type={type}
          isOpen={isAccessModalOpen}
          onClose={closeAccessModal}
          onSuccess={onUpdate}
        />
      )}
    </Fragment>
  );
}

export default observer(DriveEntityAccessStatus);
